import type { Property } from '~/core/entities/property';

import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { grammarInflection } from '~~/utils';

import { useEvents } from './events/useEvents';

interface UsePropertyPhotoCounterDeprecatedOptions {
	property: Ref<Property | null>;
}

const LEGACY_MOBILE_FIRST_PHOTO_COUNT = 1;
const LEGACY_DESKTOP_FIRST_PHOTO_BATCH_COUNT = 4;

export function usePropertyPhotoCounterDeprecated({
	property
}: UsePropertyPhotoCounterDeprecatedOptions) {
	const { trackPropertyImageScrolled } = useEvents();
	const clickCount = ref<number>(0);

	const desktopCarouselCount = ref<number | undefined>(undefined);
	const offDesktopCount = useEventBus<number | undefined>(
		EventBusKeys.desktopHeroSectionPhotoCount
	).on(count => {
		desktopCarouselCount.value = count;
	});

	const mobileCarouselCount = ref<number | undefined>(undefined);
	const offMobileCount = useEventBus<number | undefined>(
		EventBusKeys.mobileHeroSectionPhotoCount
	).on(count => {
		mobileCarouselCount.value = count;
	});

	watch(desktopCarouselCount, (newCount, oldCount) => {
		if (oldCount !== undefined && newCount !== oldCount) {
			clickCount.value = clickCount.value + 1;

			trackPropertyImageScrolled({
				[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
				[MixpanelProps.CURRENT_PAGE]: 'Property Details',
				[MixpanelProps.SECTION_NAME]: 'hero section',
				[MixpanelProps.CARD_POSITION]: newCount,
				[MixpanelProps.CARD_TYPE]: 'regular_card',
				[MixpanelProps.COMPANY_NAME]: property.value?.company.name!,
				[MixpanelProps.TAG_EXCLUSIVE]: property.value?.isExclusive!,
				[MixpanelProps.AGENT_NAME]: property.value?.agent.name!,
				[MixpanelProps.CLICK_COUNT]: clickCount.value
			});
		}
	});

	watch(mobileCarouselCount, (newCount, oldCount) => {
		if (oldCount !== undefined && newCount !== oldCount) {
			clickCount.value = clickCount.value + 1;
			trackPropertyImageScrolled({
				[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
				[MixpanelProps.CURRENT_PAGE]: 'Property Details',
				[MixpanelProps.SECTION_NAME]: 'hero section',
				[MixpanelProps.CARD_POSITION]: newCount,
				[MixpanelProps.CARD_TYPE]: 'regular_card',
				[MixpanelProps.COMPANY_NAME]: property.value?.company.name!,
				[MixpanelProps.TAG_EXCLUSIVE]: property.value?.isExclusive!,
				[MixpanelProps.AGENT_NAME]: property.value?.agent.name!,
				[MixpanelProps.CLICK_COUNT]: clickCount.value
			});
		}
	});

	const numberOfImages = computed<number>(
		() => property.value?.images.length ?? 0
	);

	const numberOfVideos = computed<number>(
		() => property.value?.videos.length ?? 0
	);

	const desktopPhotoModalCtaText = computed<string | undefined>(() => {
		const totalPhotos = numberOfImages.value;

		if (!totalPhotos) {
			return 'Fotos';
		}

		const label = grammarInflection(totalPhotos, 'foto', 'fotos');
		const defaultCount =
			totalPhotos >= LEGACY_DESKTOP_FIRST_PHOTO_BATCH_COUNT
				? LEGACY_DESKTOP_FIRST_PHOTO_BATCH_COUNT
				: totalPhotos;
		const currentPhotoCount = desktopCarouselCount.value ?? defaultCount;

		return `${currentPhotoCount}/${totalPhotos} ${label}`;
	});

	const mobilePhotoModalCtaText = computed<string | undefined>(() => {
		const totalPhotos = numberOfImages.value;
		const label = grammarInflection(totalPhotos, 'foto', 'fotos');
		const currentPhotoCount =
			mobileCarouselCount.value ?? LEGACY_MOBILE_FIRST_PHOTO_COUNT;

		return `${currentPhotoCount}/${totalPhotos} ${label}`;
	});

	onUnmounted(() => {
		offDesktopCount();
		offMobileCount();
	});

	return {
		numberOfImages,
		numberOfVideos,
		desktopPhotoModalCtaText,
		mobilePhotoModalCtaText
	};
}
